import TokenPage from '@components/token/TokenPage'
import { useViewport } from 'hooks/useViewport'
import MobileTokenPage from '@components/mobile/MobileTokenPage'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useRouter } from 'next/router'
import { fetchMetadataForMints } from 'utils/tokens'
import store from 'stores/store'
import { useEffect, useState } from 'react'
import Loading from '@components/loading/Loading'
import { TokenMetadataItem } from 'types/token'
import {
  SOLANA_TOKEN_DETAILS,
  USDC_MINT,
  USDC_TOKEN_DETAILS,
} from 'utils/constants'
import { useMarketTokensWithPrices } from 'hooks/useMarketTokens'
import { withCommonNamespaces } from 'utils/common-namespaces'
import { useQuery } from '@tanstack/react-query'

const set = store.getState().set

function Token({ baseToken }: { baseToken?: TokenMetadataItem }) {
  const router = useRouter()
  const { isDesktop } = useViewport()
  const { refetchMarketPrices } = useMarketTokensWithPrices()
  const [areTokensInitialized, setAreTokensInitialized] = useState(false)
  const baseMintInStore = store((s) => s.market.baseToken.mint)
  const connection = store((s) => s.connection)

  useEffect(() => {
    set((state) => {
      state.routeLoading = router.isFallback
    })
  }, [router.isFallback])

  const {
    data: initialTokenMetadata,
    isInitialLoading: initialTokenMetadataLoading,
  } = useQuery({
    queryKey: ['initialTokenMetadata', baseToken?.mint],
    queryFn: async () => {
      if (!baseToken) return null
      const metadata = await fetchMetadataForMints(
        [baseToken.mint],
        undefined,
        connection,
      )
      return metadata?.[0] || null
    },
    enabled:
      !areTokensInitialized && !!baseToken?.mint && !baseToken.token_program,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!baseToken || initialTokenMetadataLoading || areTokensInitialized)
      return

    const baseTokenWithMetadata = {
      ...baseToken,
      ...initialTokenMetadata,
    }

    set((state) => {
      state.market.baseToken = baseTokenWithMetadata
      state.swap.outputToken = baseTokenWithMetadata
      state.trade.limitForm.price = ''

      const newQuoteToken =
        baseToken.mint === USDC_MINT ? SOLANA_TOKEN_DETAILS : USDC_TOKEN_DETAILS
      state.market.quoteToken = newQuoteToken
      state.swap.inputToken = newQuoteToken
    })
    void refetchMarketPrices()
    setAreTokensInitialized(true)
  }, [
    areTokensInitialized,
    baseToken,
    initialTokenMetadata,
    initialTokenMetadataLoading,
    refetchMarketPrices,
  ])

  useEffect(() => {
    if (!baseToken) return

    // get latest value from store, which may have been updated by another effect
    const baseMintInStore = store.getState().market.baseToken.mint

    if (!router.asPath.includes(`/token/${baseMintInStore}`)) {
      const { mint: _, ...rest } = router.query
      void router.replace(
        {
          pathname: `/token/${baseMintInStore}`,
          query: rest,
        },
        undefined,
        {
          shallow: true,
        },
      )
    }
  }, [baseToken, baseMintInStore, router])

  if (router?.isFallback || !areTokensInitialized) {
    return (
      <div className="flex h-[calc(100vh-368px)] flex-col items-center justify-center">
        <Loading />
      </div>
    )
  }
  return isDesktop ? <TokenPage /> : <MobileTokenPage />
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true, // pages will be generated on-demand
  }
}

export async function getStaticProps({
  params,
  locale,
}: {
  params: { mint: string }
  locale: string
}) {
  const metadata = await fetchMetadataForMints([params.mint])
  const metaTitle = `Trade ${
    metadata?.[0]?.symbol ? metadata[0].symbol : ''
  } | Cabana`
  const baseToken: TokenMetadataItem = {
    ...metadata?.[0],
    mint: params.mint,
  }
  return {
    props: {
      ...(await serverSideTranslations(
        locale,
        withCommonNamespaces([
          'chart',
          'orderbook',
          'swap',
          'token-page',
          'trade',
        ]),
      )),
      baseToken,
      metaTitle,
    },
  }
}

export default Token
